import { IconSettingsSliderThree } from 'central-icons/IconSettingsSliderThree';
import { useCallback } from 'react';

import { Badge, useDisclosure } from '@revelio/ui';

import { useCustomDashPreferenceVisibility } from '../../../../shared/user-preferences/hooks/useCustomDashPreferenceVisibility';
import { PreferencesModal } from '../../../../shared/user-preferences/PreferencesModal';
import type { OnPreferenceUpdateCallback } from '../../../../shared/user-preferences/preference-editors/preferenceEditors';
import {
  type PreferenceScope,
  UserPreferenceKey,
  type UserPreferenceSubKey,
  type UserPreferences,
} from '../../../../shared/user-preferences/types';
import { UserPreferencesDataProvider } from '../../../../shared/user-preferences/UserPreferencesDataProvider';
import { deriveEffectiveTaxonomiesForScope } from '../../../../shared/user-preferences/utils/derive-effective-taxonomies';
import { calculateVisiblePreferenceDiff } from '../../../../shared/user-preferences/utils/preference-diff';
import { filtersToPreferences } from '../../../../shared/user-preferences/utils/preference-filter-adapters';
import {
  getPreferenceScope,
  updateCustomDashFiltersForPreference,
} from '../../../../shared/user-preferences/utils/preferences';
import { getVisiblePreferenceKeys } from '../../../../shared/user-preferences/utils/visibility';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { getConsistencyKey } from '../../filters/utils/consistencyKey';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { DataProviderQueryStatus } from '../DataProvider/types';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../PageConfigProvider/types';
import { ActionBarIconButton } from './ActionBarIconButton';
import styles from './UserPreferencesButton.module.css';

export const UserPreferencesButtonBase = ({
  affectedScope,
  preferencesFromFilters,
  onPreferenceUpdate,
  preferenceVisibility,
}: {
  affectedScope: PreferenceScope;
  preferencesFromFilters?: Partial<UserPreferences>;
  onPreferenceUpdate: OnPreferenceUpdateCallback;
  preferenceVisibility?: (key: UserPreferenceKey) => boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Scoped settings (Default Filters, Analytics Tabs) no longer depend on the
  // page's own scope — the modal's scope switcher supplies the scope — so
  // visibility is left entirely to the caller's flag/page gating.
  const effectivePreferenceVisibility = useCallback(
    (key: UserPreferenceKey): boolean =>
      preferenceVisibility ? preferenceVisibility(key) : true,
    [preferenceVisibility]
  );

  return (
    <UserPreferencesDataProvider
      affectedScope={affectedScope}
      preferencesFromFilters={preferencesFromFilters}
    >
      {({ data, status }) => {
        if (!data) return null;
        if (status !== DataProviderQueryStatus.Success) {
          return (
            <>
              <ActionBarIconButton
                icon={IconSettingsSliderThree}
                aria-label="Preferences"
                onClick={onOpen}
                tooltipLabel="Preferences"
              />
              <PreferencesModal
                isOpen={isOpen}
                onClose={onClose}
                affectedScope={affectedScope}
                preferencesFromFilters={preferencesFromFilters}
                onPreferenceUpdate={onPreferenceUpdate}
                preferenceVisibility={effectivePreferenceVisibility}
              />
            </>
          );
        }

        const visiblePreferenceKeys = getVisiblePreferenceKeys(
          effectivePreferenceVisibility
        );
        const diff = calculateVisiblePreferenceDiff(
          data.preferences,
          data.savedPreferences,
          visiblePreferenceKeys
        );
        const hasDiff = diff.length > 0;

        const renderButton = () => {
          return (
            <div className={styles.badgedButton}>
              <ActionBarIconButton
                icon={IconSettingsSliderThree}
                aria-label="Preferences"
                onClick={onOpen}
                tooltipLabel="Preferences"
              />
              {hasDiff && (
                <div className={styles.diffWrap}>
                  <Badge
                    className={styles.diffDot}
                    size="sm"
                    data-testid="preference-diff-indicator"
                  />
                </div>
              )}
            </div>
          );
        };

        return (
          <>
            {renderButton()}
            <PreferencesModal
              isOpen={isOpen}
              onClose={onClose}
              affectedScope={affectedScope}
              preferencesFromFilters={preferencesFromFilters}
              onPreferenceUpdate={onPreferenceUpdate}
              preferenceVisibility={effectivePreferenceVisibility}
            />
          </>
        );
      }}
    </UserPreferencesDataProvider>
  );
};

export const UserPreferencesButton = () => {
  const { activeFilterMap, filterState, dispatch } = useFilterContext();
  const basePreferenceVisibility = useCustomDashPreferenceVisibility();
  const { revelioPageId, config } = useTabContentConfig();

  const pageGroupKey = getConsistencyKey({
    revelioPageId,
    primaryFilterId: config.primaryFilter?.id,
  });
  const isFilterGroupSynced = filterState.activeKey === pageGroupKey;

  const affectedScope = getPreferenceScope({
    pageId: revelioPageId,
    primaryFilterId: pageGroupKey,
  });

  const preferenceVisibility = useCallback(
    (key: UserPreferenceKey): boolean => {
      // Posting Discovery: only show the Taxonomies preference.
      if (revelioPageId === RevelioPageId.POSTING_DISCOVERY) {
        return key === UserPreferenceKey.Taxonomies;
      }
      if (isScreenerV2Page(revelioPageId)) {
        if (
          key === UserPreferenceKey.DefaultFilters ||
          key === UserPreferenceKey.Taxonomies
        ) {
          return false;
        }
      }
      return basePreferenceVisibility(key);
    },
    [basePreferenceVisibility, revelioPageId]
  );

  const onPreferenceUpdate = useCallback(
    (
      preference: UserPreferenceKey,
      value: UserPreferences[UserPreferenceKey],
      subKey?: UserPreferenceSubKey
    ) => {
      // Guard: never apply preference-controlled filter updates to the wrong group.
      if (!isFilterGroupSynced) {
        return;
      }
      // Adaptive is only resolvable on COMPANY pages — everywhere else the
      // global choice materializes as the Generic taxonomy.
      const effectiveValue =
        preference === UserPreferenceKey.Taxonomies
          ? deriveEffectiveTaxonomiesForScope(
              value as UserPreferences[UserPreferenceKey.Taxonomies],
              affectedScope
            )
          : value;
      updateCustomDashFiltersForPreference(
        dispatch,
        preference,
        effectiveValue as UserPreferences[UserPreferenceKey],
        subKey
      );
    },
    [dispatch, isFilterGroupSynced, affectedScope]
  );

  const preferencesFromFilters = filtersToPreferences({
    activeFilterMap,
    affectedScope,
  });

  // Prevent opening the preferences UI while filter group switching is in-flight.
  // Otherwise edits can be applied to the previous group's filters/preferences.
  if (!isFilterGroupSynced) {
    return (
      <ActionBarIconButton
        icon={IconSettingsSliderThree}
        aria-label="Preferences"
        tooltipLabel="Preferences"
        isDisabled
      />
    );
  }

  return (
    <UserPreferencesButtonBase
      affectedScope={affectedScope}
      preferencesFromFilters={preferencesFromFilters}
      onPreferenceUpdate={onPreferenceUpdate}
      preferenceVisibility={preferenceVisibility}
    />
  );
};
